import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";

import Login from "@/pages/Login";
import AdminLayout from "@/layouts/AdminLayout";
import CompanyLayout from "@/layouts/CompanyLayout";
import StockLayout from "@/layouts/StockLayout";

// Sayfalar ihtiyaç anında yüklenir (code splitting) → ilk açılış çok daha hızlı
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminCompanies = lazy(() => import("@/pages/admin/Companies"));
const AdminCompanyDetail = lazy(() => import("@/pages/admin/CompanyDetail"));
const AdminProducts = lazy(() => import("@/pages/admin/Products"));
const AdminProductEdit = lazy(() => import("@/pages/admin/ProductEdit"));
const AdminCategories = lazy(() => import("@/pages/admin/Categories"));
const AdminTaxonomy = lazy(() => import("@/pages/admin/Taxonomy"));
const AdminStock = lazy(() => import("@/pages/admin/Stock"));
const AdminOrders = lazy(() => import("@/pages/admin/Orders"));
const AdminOrderDetail = lazy(() => import("@/pages/admin/OrderDetail"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminAuditLogs = lazy(() => import("@/pages/admin/AuditLogs"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));

const CompanyHome = lazy(() => import("@/pages/company/Home"));
const CompanyProductsAll = lazy(() => import("@/pages/company/ProductsAll"));
const CompanyCariHesap = lazy(() => import("@/pages/company/CariHesap"));
const CompanyProductDetail = lazy(() => import("@/pages/company/ProductDetail"));
const CompanyCart = lazy(() => import("@/pages/company/Cart"));
const CompanyOrders = lazy(() => import("@/pages/company/Orders"));
const CompanyOrderDetail = lazy(() => import("@/pages/company/OrderDetail"));
const CompanyAccount = lazy(() => import("@/pages/company/Account"));

const StockDashboard = lazy(() => import("@/pages/stock/StockDashboard"));

const PageFallback = () => (
  <div className="py-16 text-center text-sm text-slate-500 animate-pulse">Yükleniyor…</div>
);

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Yükleniyor…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "stock_staff") return <Navigate to="/stok" replace />;
  return <Navigate to="/b2b" replace />;
}

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-slate-400">Yükleniyor…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />

            <Route path="/admin" element={<Protected roles={["admin"]}><AdminLayout /></Protected>}>
              <Route index element={<AdminDashboard />} />
              <Route path="firmalar" element={<AdminCompanies />} />
              <Route path="firmalar/:id" element={<AdminCompanyDetail />} />
              <Route path="urunler" element={<AdminProducts />} />
              <Route path="urunler/yeni" element={<AdminProductEdit />} />
              <Route path="urunler/:id" element={<AdminProductEdit />} />
              <Route path="kategoriler" element={<AdminCategories />} />
              <Route path="taksonomi" element={<AdminTaxonomy />} />
              <Route path="stok" element={<AdminStock />} />
              <Route path="siparisler" element={<AdminOrders />} />
              <Route path="siparisler/:id" element={<AdminOrderDetail />} />
              <Route path="kullanicilar" element={<AdminUsers />} />
              <Route path="hareketler" element={<AdminAuditLogs />} />
              <Route path="ayarlar" element={<AdminSettings />} />
            </Route>

            <Route path="/b2b" element={<Protected roles={["company_user"]}><CompanyLayout /></Protected>}>
              <Route index element={<CompanyHome />} />
              <Route path="urunler" element={<CompanyProductsAll />} />
              <Route path="cari-hesap" element={<CompanyCariHesap />} />
              <Route path="urun/:id" element={<CompanyProductDetail />} />
              <Route path="sepet" element={<CompanyCart />} />
              <Route path="siparisler" element={<CompanyOrders />} />
              <Route path="siparisler/:id" element={<CompanyOrderDetail />} />
              <Route path="hesabim" element={<CompanyAccount />} />
            </Route>

            <Route path="/stok" element={<Protected roles={["stock_staff"]}><StockLayout /></Protected>}>
              <Route index element={<StockDashboard />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <Toaster theme="dark" position="bottom-right" richColors duration={2000} closeButton />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
